/**
 * QuickBracketEntriesPage — standalone window for Quick Bracket Entries.
 *
 * Opened via window.open() from BookieLeagueManager so the user can run
 * Quick Entries and Quick Bracket Entries side-by-side in a split screen.
 *
 * URL: /bookie/quick-bracket-entries/:leagueId/:squadId
 *
 * After saving, broadcasts on the "bookie-entries-refresh" BroadcastChannel
 * so the main window knows to reload its data, then closes this window.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import QuickBracketEntriesModal from './QuickBracketEntriesModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

interface SideEvent {
  id: number;
  name: string;
  type: string;
  cost: number;
  entry_count: number;
  bracket_size?: number;
  squad_id: number;
  gender?: string | null;
  min_age?: number | null;
  max_age?: number | null;
}

const broadcast = (leagueId: string, squadId: string) => {
  try {
    const ch = new BroadcastChannel('bookie-entries-refresh');
    ch.postMessage({ leagueId, squadId });
    ch.close();
  } catch { /* BroadcastChannel not supported — silently ignore */ }
};

const QuickBracketEntriesPage: React.FC = () => {
  const { leagueId, squadId } = useParams<{ leagueId: string; squadId: string }>();
  const [sideEvents, setSideEvents] = useState<SideEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!leagueId) return;
    axios.get(`${API}/bookie-league-management/leagues/${leagueId}`, { headers: authHeader() })
      .then(r => {
        const all: SideEvent[] = r.data.sideEvents || [];
        // Filter to bracket events for the selected squad only
        setSideEvents(squadId ? all.filter(se => String(se.squad_id) === squadId && se.type === 'bracket') : all.filter(se => se.type === 'bracket'));
      })
      .catch(() => setError('Failed to load league data. Make sure you are logged in.'))
      .finally(() => setLoading(false));
  }, [leagueId, squadId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-center p-8">
        <div>
          <p className="text-red-400 mb-3">{error}</p>
          <button onClick={() => window.close()} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg">
            Close Window
          </button>
        </div>
      </div>
    );
  }

  return (
    <QuickBracketEntriesModal
      leagueId={Number(leagueId)}
      squadId={Number(squadId)}
      sideEvents={sideEvents}
      standalone
      onClose={() => window.close()}
      onRefresh={() => {
        broadcast(leagueId!, squadId!);
        window.close();
      }}
    />
  );
};

export default QuickBracketEntriesPage;
